import { useEffect, useState } from 'react'
import { Group, SimpleGrid, Box } from '@mantine/core'
import { AppButton, AppCheckbox, AppModal, AppText } from '../../app/common/ui'
import agent from '../../app/api/agent'
import { Part } from '../../app/models/part'
import { FirearmPartFormValues } from '../../app/models/firearm'

function formatCurrency (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function partToFormValues (p: Part): FirearmPartFormValues {
  return {
    partId: p.id,
    name: p.name ?? '',
    description: p.description ?? '',
    partNumber: p.partNumber ?? '',
    manufacturer: p.manufacturer ?? '',
    value: p.value ?? 0,
    condition: p.condition ?? '',
    installDate: '',
    notes: p.notes ?? '',
    imageUrl: p.imageUrl ?? undefined
  }
}

function isAlreadyAdded (part: Part, existing: FirearmPartFormValues[]): boolean {
  return existing.some(
    (e) => e.partId === part.id || (e.name === part.name && (e.partNumber || '') === (part.partNumber || ''))
  )
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onSelectParts: (parts: FirearmPartFormValues[]) => void;
  existingParts?: FirearmPartFormValues[];
}

export default function PartsBinPickerModal ({
  opened,
  onClose,
  onSelectParts,
  existingParts = []
}: Props) {
  const [parts, setParts] = useState<Part[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (opened) {
      setLoading(true)
      setSelectedIds(new Set())
      agent.Parts.list()
        .then((result) => setParts(result.items))
        .finally(() => setLoading(false))
    }
  }, [opened])

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAdd = () => {
    const toAdd = parts
      .filter((p) => selectedIds.has(p.id) && !isAlreadyAdded(p, existingParts))
      .map(partToFormValues)
    onSelectParts(toAdd)
    onClose()
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Add from parts bin"
      size="lg"
      styles={{ modal: { borderRadius: 12, padding: 'var(--gs-lg)' }, header: { marginBottom: 'var(--gs-md)' } }}
    >
      {loading
        ? (
        <AppText color="dimmed" size="sm">
          Loading parts...
        </AppText>
          )
        : parts.length === 0
          ? (
        <AppText color="dimmed" size="sm">
          No parts in your bin. Add parts in the Parts Bin first.
        </AppText>
            )
          : (
        <>
          <SimpleGrid
            cols={1}
            breakpoints={[{ minWidth: 400, cols: 2 }, { minWidth: 640, cols: 3 }]}
            spacing="md"
            mb="md"
          >
            {parts.map((part) => {
              const alreadyAdded = isAlreadyAdded(part, existingParts)
              const checked = selectedIds.has(part.id)
              return (
                <Box
                  key={part.id}
                  sx={{
                    padding: 12,
                    borderRadius: 12,
                    background: 'var(--gs-surface)',
                    border: '1px solid var(--gs-border)',
                    cursor: alreadyAdded ? 'not-allowed' : 'pointer',
                    opacity: alreadyAdded ? 0.7 : 1
                  }}
                  onClick={() => !alreadyAdded && toggle(part.id)}
                >
                  <Group position="apart" noWrap spacing="xs" mb={4}>
                    <AppCheckbox
                      checked={checked}
                      disabled={alreadyAdded}
                      onChange={() => !alreadyAdded && toggle(part.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {alreadyAdded && (
                      <AppText size="xs" color="dimmed">
                        Already added
                      </AppText>
                    )}
                  </Group>
                  <AppText weight={600} size="sm" lineClamp={1}>
                    {part.name}
                  </AppText>
                  {(part.manufacturer || part.partNumber) && (
                    <AppText size="xs" color="dimmed" mb={4}>
                      {[part.manufacturer, part.partNumber].filter(Boolean).join(' · ')}
                    </AppText>
                  )}
                  <AppText weight={600} size="sm" sx={{ color: 'var(--gs-gold)' }}>
                    {formatCurrency(part.value)}
                  </AppText>
                </Box>
              )
            })}
          </SimpleGrid>
          <Group position="right">
            <AppButton variant="default" onClick={onClose}>
              Cancel
            </AppButton>
            <AppButton
              onClick={handleAdd}
              disabled={selectedIds.size === 0}
            >
              Add selected to firearm
            </AppButton>
          </Group>
        </>
            )}
    </AppModal>
  )
}
